import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Calendar, Linkedin, Twitter } from 'lucide-react';
import SEO, { generateArticleSchema } from '@/components/SEO';

const articles = {
  'gmp-facility-design-considerations': {
    title: 'Key Considerations in Designing cGMP-Compliant Pharma Facilities',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 22, 2026',
    readTime: '6 min read',
    image: '/images/external/photo-1559757148-5c350d0d3c56.jpg',
    content: `
      <p class="lead">Designing a pharmaceutical facility is not just about construction — it is about compliance, efficiency, and long-term operability. A poorly designed facility can lead to regulatory observations, operational inefficiencies, and costly modifications years down the line.</p>

      <h2>1. Proper Layout Planning</h2>
      <ul>
        <li>Logical material flow from receiving to dispatch</li>
        <li>Separate personnel movement paths</li>
        <li>Avoid cross-contamination through clear segregation</li>
      </ul>

      <h2>2. Cleanroom Classification</h2>
      <ul>
        <li>Define area grades (A, B, C, D) based on operations</li>
        <li>Align classification with product requirements and process risk</li>
      </ul>

      <h2>3. HVAC Design</h2>
      <ul>
        <li>Pressure cascades to prevent reverse airflow</li>
        <li>Temperature & humidity control tied to product stability</li>
        <li>Filtration levels (HEPA, ULPA) selected per cleanroom grade</li>
      </ul>

      <h2>4. Utility Planning</h2>
      <ul>
        <li>Compressed air — oil-free, dried, and filtered</li>
        <li>Water systems — PW / WFI with proper loop design</li>
        <li>Electrical reliability with redundancy for critical loads</li>
      </ul>

      <h2>5. Documentation & Validation</h2>
      <ul>
        <li>User Requirement Specifications (URS) defined at concept stage</li>
        <li>IQ / OQ planning integrated with construction milestones</li>
        <li>SOP preparation aligned with equipment delivery</li>
      </ul>

      <h2>6. Maintenance-Friendly Design</h2>
      <ul>
        <li>Accessibility to filters, valves, and instruments</li>
        <li>Ease of cleaning — coved corners, sealed joints</li>
        <li>Future expansion provisions built into the original layout</li>
      </ul>

      <h2>Conclusion</h2>
      <p>A cGMP-compliant facility is not achieved by chance — it is achieved by careful engineering and planning. The best facilities are those designed with both compliance and operations in mind from day one.</p>

      <p>At H&G, our Pharma Engineering team designs greenfield, brownfield, and capacity-expansion facilities with deep cGMP and operational expertise. Contact us to discuss your facility plan.</p>
    `
  },
  'why-pmc-critical-pharma-projects': {
    title: 'Why Project Management Consultancy (PMC) is Critical for Pharma Projects',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 22, 2026',
    readTime: '5 min read',
    image: '/images/external/photo-1565793298595-6a879b1d9492.jpg',
    content: `
      <p class="lead">Pharmaceutical projects are highly complex, involving multiple disciplines — HVAC, utilities, cleanrooms, compliance, and validation. Without proper coordination, projects often suffer from delays, cost overruns, and compliance issues. This is where PMC plays a crucial role.</p>

      <h2>What is PMC?</h2>
      <p>PMC acts as the client's technical and management representative, ensuring the project is executed as per defined scope, budget, and timeline. The PMC is the single point of accountability across all vendors, contractors, and internal stakeholders.</p>

      <h2>Key Challenges in Pharma Projects</h2>
      <ul>
        <li>Multiple vendors and agencies working in parallel</li>
        <li>Complex cGMP requirements across utilities and cleanrooms</li>
        <li>Design changes during execution that ripple across systems</li>
        <li>Poor coordination between civil, mechanical, electrical, and instrumentation teams</li>
      </ul>

      <h2>How PMC Solves These Issues</h2>

      <h3>1. Better Planning</h3>
      <p>Proper project scheduling with defined milestones, dependencies, and critical-path management.</p>

      <h3>2. Vendor Coordination</h3>
      <p>Technical evaluation of vendors, avoiding wrong selection at the start, and ongoing performance management.</p>

      <h3>3. Quality Control</h3>
      <p>Ensuring cGMP compliance through regular site monitoring and design-review checkpoints.</p>

      <h3>4. Cost Control</h3>
      <p>Avoiding unnecessary changes through rigorous change management and ongoing budget tracking.</p>

      <h3>5. Risk Management</h3>
      <p>Identifying issues early — before they cascade into delays — and proactively reducing project risk.</p>

      <h2>Real Industry Insight</h2>
      <p>Many pharma projects fail not because of poor contractors, but due to lack of coordination and planning. The PMC bridges this gap.</p>

      <h2>Conclusion</h2>
      <p>PMC is not an additional cost — it is a risk-reduction investment. The ROI shows up as on-time commissioning, audit-ready facilities, and operations-friendly engineering.</p>

      <p>At H&G, we provide end-to-end PMC services with 25+ years of pharma engineering experience. Contact us to discuss your next project.</p>
    `
  },
  'pharma-hvac-design-mistakes': {
    title: 'Common Mistakes in Pharma HVAC Design (And How to Avoid Them)',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 22, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1581092580497-e0d23cbdf1dc.jpg',
    content: `
      <p class="lead">HVAC systems are the backbone of any pharmaceutical facility. From maintaining cleanroom classifications to ensuring pressure cascades and contamination control, HVAC design directly impacts product quality and regulatory compliance. However, many pharma projects face serious issues due to poor HVAC design decisions made at the initial stage.</p>

      <h2>1. Incorrect Pressure Cascade Design</h2>
      <p>One of the most critical aspects of cleanroom HVAC is the pressure differential between zones.</p>
      <ul>
        <li><strong>Common mistake:</strong> Not defining the pressure hierarchy clearly, or improper zoning of areas.</li>
        <li><strong>Impact:</strong> Cross-contamination risk and major audit observations.</li>
        <li><strong>Best practice:</strong> Define the pressure cascade during concept stage and validate it during design review.</li>
      </ul>

      <h2>2. Improper Air Change Rate Selection</h2>
      <ul>
        <li><strong>Common mistake:</strong> Copy-paste of air change values without understanding the process.</li>
        <li><strong>Impact:</strong> Overdesign drives higher energy cost; underdesign creates compliance risk.</li>
        <li><strong>Best practice:</strong> Calculate based on room volume, heat load, and cleanroom classification.</li>
      </ul>

      <h2>3. Poor Airflow Pattern Design</h2>
      <ul>
        <li><strong>Common mistake:</strong> Incorrect placement of supply and return air, or ignoring airflow direction.</li>
        <li><strong>Impact:</strong> Dead zones and contamination risk.</li>
        <li><strong>Best practice:</strong> Ensure proper airflow visualization and conduct smoke studies.</li>
      </ul>

      <h2>4. Ignoring Heat Load Calculations</h2>
      <ul>
        <li><strong>Common mistake:</strong> Not considering equipment heat load or personnel heat load.</li>
        <li><strong>Impact:</strong> Temperature fluctuation and HVAC system failure.</li>
      </ul>

      <h2>5. No Provision for Maintenance</h2>
      <ul>
        <li><strong>Common mistake:</strong> No access to filters, AHU components, or critical valves.</li>
        <li><strong>Impact:</strong> Difficult maintenance and increased downtime.</li>
        <li><strong>Best practice:</strong> Design HVAC with maintenance accessibility in mind from the start.</li>
      </ul>

      <h2>Conclusion</h2>
      <p>Most HVAC problems originate at the design stage, not during execution. A practical, experience-driven design approach can prevent costly modifications later.</p>

      <p>At H&G, our Pharma Engineering team brings 25+ years of HVAC and utilities expertise across cleanroom and cGMP environments. Contact us for an HVAC design review or audit.</p>
    `
  },
  'why-pharma-projects-delayed': {
    title: 'Why Pharma Projects Get Delayed (And How to Prevent It)',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '6 min read',
    image: '/images/external/photo-1587854692152-cbe660dbde88.jpg',
    content: `
      <p class="lead">Pharma project delays are rarely caused by a single big failure. They are the cumulative result of small, ignored issues at design, vendor selection, and execution stages. Recognising the early signals is the difference between on-time commissioning and a 6-month slip.</p>

      <h2>1. Incomplete URS at Concept Stage</h2>
      <p>An incomplete User Requirement Specification is the single biggest reason for downstream design changes. Every change after engineering freeze cascades into vendor re-quotes, layout edits, and validation re-runs.</p>

      <h2>2. Wrong Vendor Selection</h2>
      <ul>
        <li>Selecting on lowest price instead of pharma capability</li>
        <li>Skipping vendor capability audits</li>
        <li>Ignoring past performance and reference checks</li>
      </ul>

      <h2>3. Poor Inter-Disciplinary Coordination</h2>
      <p>Civil, mechanical, electrical, HVAC, and instrumentation teams working in silos create interface conflicts that surface only at site — by which time, rework is expensive.</p>

      <h2>4. Inadequate Long-Lead Item Planning</h2>
      <p>AHUs, chillers, custom skids, and validation instruments have 16–24 week lead times. Ordering them based on detailed engineering instead of concept-stage commitment is a common cause of 2–3 month slippages.</p>

      <h2>5. Ignoring Change Control</h2>
      <p>Uncontrolled scope changes during execution — driven by user departments, marketing, or board direction — derail the critical path. Strong change-control discipline is non-negotiable.</p>

      <h2>How to Prevent It</h2>
      <ul>
        <li>Lock URS before engineering — and resist mid-project changes</li>
        <li>Run vendor capability audits before order placement</li>
        <li>Conduct weekly interface review meetings across disciplines</li>
        <li>Pre-commit long-lead items at concept stage</li>
        <li>Empower PMC to enforce change control discipline</li>
      </ul>

      <h2>Conclusion</h2>
      <p>Project delays are a leadership problem before they are a technical problem. The fix is upstream discipline — not downstream firefighting.</p>

      <p>At H&G, our Pharma Engineering & PMC practice has executed projects across Indian and overseas geographies with on-time and audit-clean track record. Contact us for project recovery, planning, or PMC support.</p>
    `
  },
  'cleanroom-design-mistakes': {
    title: 'Common Mistakes in Cleanroom Design',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '6 min read',
    image: '/images/external/photo-1532187863486-abf9dbad1b69.jpg',
    content: `
      <p class="lead">Cleanroom design is unforgiving. Small errors at concept stage cascade into compliance failures, contamination risks, and operational nightmares for the next 15–20 years of the facility's life.</p>

      <h2>1. Incorrect Area Classification</h2>
      <p>Over-classifying creates unnecessary capex; under-classifying creates compliance risk. The right approach is process-driven — map each operation to its true exposure risk before assigning Grade A/B/C/D.</p>

      <h2>2. Poor Material & Personnel Flow</h2>
      <ul>
        <li>Cross-overs between clean and dirty paths</li>
        <li>No dedicated material airlocks</li>
        <li>Gowning rooms sized for staff at rest, not at peak shift</li>
      </ul>

      <h2>3. Inadequate Airlock Design</h2>
      <p>Single-door airlocks defeating the pressure cascade. Material airlocks doubling as personnel airlocks. These are the most common 483 observations on Indian and US-FDA audits.</p>

      <h2>4. Wrong Wall, Floor, Ceiling Selection</h2>
      <ul>
        <li>Non-coved corners that harbour contamination</li>
        <li>Floors with insufficient chemical resistance</li>
        <li>Ceilings with poor access for HEPA replacement</li>
      </ul>

      <h2>5. Insufficient Maintenance Access</h2>
      <p>HEPA boxes, dampers, sensors, and lighting fixtures that cannot be accessed without taking the room out of service create chronic operational pain.</p>

      <h2>6. Ignoring Smoke Studies at Design Stage</h2>
      <p>Smoke studies done only at qualification often reveal airflow problems that should have been caught at design — by which point, retrofit is expensive.</p>

      <h2>Conclusion</h2>
      <p>Great cleanrooms are not pretty rooms — they are practical, maintainable, compliant rooms. The best ones are designed by engineers who have lived inside operating plants.</p>

      <p>At H&G, we design cleanrooms for sterile, non-sterile, OSD, biotech, and nutraceutical facilities. Contact us for cleanroom design, review, or qualification support.</p>
    `
  },
  'utility-systems-fail-pharma': {
    title: 'Why Utility Systems Fail in Pharma Plants',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1518770660439-4636190af475.jpg',
    content: `
      <p class="lead">Compressed air, water, steam, and HVAC utilities are the silent backbone of every pharma plant. When they fail, production halts and compliance suffers. The failure modes are predictable — and preventable.</p>

      <h2>1. Under-Sized at Day Zero</h2>
      <p>Capacity calculations done on nameplate ratings instead of true operating load. The plant runs fine in year one, then collapses when a second line is added.</p>

      <h2>2. No Redundancy on Critical Loads</h2>
      <p>N+1 design is non-negotiable for compressed air, chilled water, and PW. Most failures we see are facilities running with N-design because "the project was tight on budget".</p>

      <h2>3. Poor Material of Construction Choices</h2>
      <ul>
        <li>Carbon steel instead of stainless on critical loops</li>
        <li>Wrong elastomer grades on PW gaskets</li>
        <li>Insufficient pipe slope causing dead-leg formation</li>
      </ul>

      <h2>4. Missing Sanitization Strategy</h2>
      <p>PW and WFI loops designed without a clear sanitization frequency, method, and validation protocol. The microbial counts creep up; audits catch it; the loop has to be retrofitted.</p>

      <h2>5. Inadequate Instrumentation</h2>
      <p>Critical parameters (conductivity, TOC, pressure differential, dewpoint) not continuously monitored. When a deviation happens, there is no data trail to investigate.</p>

      <h2>6. Maintenance Neglect</h2>
      <p>Filter change schedules, lubrication intervals, and PM frequencies skipped to meet production targets. Six months of skipped PM creates twelve months of breakdown.</p>

      <h2>Conclusion</h2>
      <p>Utility failures are not utility problems. They are design, procurement, and maintenance discipline problems. The fix is upstream and ongoing.</p>

      <p>At H&G, we audit and redesign utility systems — compressed air, water, steam, HVAC — across pharma and nutraceutical plants. Contact us for a utility audit or redesign.</p>
    `
  },
  'top-7-gmp-facility-mistakes': {
    title: 'Top 7 Mistakes in cGMP Facility Planning',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1576091160399-112ba8d25d1d.jpg',
    content: `
      <p class="lead">Most cGMP facility projects start with great intent but stumble on the same seven mistakes — layout, classification, utilities, documentation, automation, change control, and validation planning. Recognising them upfront saves crores in rework.</p>

      <h2>1. Inadequate Site Master Plan</h2>
      <p>Building Phase 1 without thinking about Phase 2 and 3. Future expansion zones not blocked. Utility headers not sized for expansion load.</p>

      <h2>2. Wrong Area Classification</h2>
      <p>Classifying based on perceived comfort rather than process risk. Either over-classifying (waste of capex) or under-classifying (audit risk).</p>

      <h2>3. Utility Sizing on Nameplate Ratings</h2>
      <p>Calculating compressed air, chilled water, and electrical loads on equipment nameplate ratings instead of true operating diversity factors.</p>

      <h2>4. Documentation as After-Thought</h2>
      <p>URS, FDS, HDS, DQ, IQ, OQ, PQ done sequentially after construction. Should be integrated with engineering from day one.</p>

      <h2>5. No Automation Strategy</h2>
      <p>BMS, EMS, SCADA, and SAP integration treated as later add-ons. Result: silos, data integrity issues, and audit findings.</p>

      <h2>6. Weak Change Control</h2>
      <p>Mid-project changes accepted without formal impact assessment. Cost, schedule, and validation impacts not documented.</p>

      <h2>7. Validation Plan Built Backward</h2>
      <p>Validation strategy built after IQ instead of being defined at URS stage. This creates re-work, re-tests, and protocol revisions.</p>

      <h2>Conclusion</h2>
      <p>cGMP facility planning is a discipline, not a checklist. The best facilities are planned by teams who understand operations, audits, and engineering as one integrated practice.</p>

      <p>At H&G, our cGMP facility planning practice covers OSD, sterile, biotech, API, and nutraceutical sectors. Contact us for site master planning or facility design review.</p>
    `
  },
  'pressure-cascade-cleanrooms-guide': {
    title: 'Understanding Pressure Cascade in Cleanrooms (A Simple Practical Guide)',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '6 min read',
    image: '/images/external/photo-1559757148-5c350d0d3c56.jpg',
    content: `
      <p class="lead">Pressure cascade is the most misunderstood part of cleanroom HVAC. This is the practical, field-tested approach we use across audits and design reviews — no jargon, no theory, just what works.</p>

      <h2>The Core Principle</h2>
      <p>Pressure cascade is about controlling airflow direction. Air must always flow from cleaner zones to less-clean zones, never the reverse. The differential is the engineering tool that enforces this.</p>

      <h2>The Standard Hierarchy</h2>
      <ul>
        <li>Grade A → Grade B: 10–15 Pa</li>
        <li>Grade B → Grade C: 10–15 Pa</li>
        <li>Grade C → Grade D: 10–15 Pa</li>
        <li>Grade D → Unclassified: 10–15 Pa</li>
      </ul>

      <h2>Where Designs Go Wrong</h2>
      <ol>
        <li><strong>Airlocks at the same pressure as the room they serve</strong> — defeating the cascade.</li>
        <li><strong>Return air paths that bypass the pressure hierarchy</strong> — causing reverse flow.</li>
        <li><strong>Insufficient supply-return offset</strong> — making the cascade unstable when doors open.</li>
        <li><strong>No dampers on return ducts</strong> — making fine-tuning impossible.</li>
      </ol>

      <h2>Practical Field Checks</h2>
      <ul>
        <li>Manometer readings logged at every airlock — at-rest and in-operation</li>
        <li>Smoke studies at every transition — verify direction visually</li>
        <li>Door-opening recovery test — pressure should restore in &lt; 30 seconds</li>
      </ul>

      <h2>What Inspectors Look For</h2>
      <p>USFDA, EU cGMP, and WHO inspectors will ask for: pressure logs, smoke study video, recovery test data, and the rationale for the chosen differential. Have these documented before the audit, not during.</p>

      <h2>Conclusion</h2>
      <p>Pressure cascade is not exotic engineering — it is disciplined airflow management. Get it right at design, validate it at qualification, monitor it daily, and the audit takes care of itself.</p>

      <p>At H&G, we conduct pressure-cascade reviews and smoke-study planning for sterile and non-sterile facilities. Contact us for an audit-readiness assessment.</p>
    `
  },
  'compressed-air-system-pharma': {
    title: 'How to Design a Reliable Compressed Air System for Pharma',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1565793298595-6a879b1d9492.jpg',
    content: `
      <p class="lead">Compressed air is a critical cGMP utility — and it is the most under-engineered system on the plant. From compressor sizing to ISO 8573 quality classes, here is the design playbook that works.</p>

      <h2>1. Capacity Sizing Done Right</h2>
      <ul>
        <li>List every air-consuming user with FAD requirement</li>
        <li>Apply a diversity factor (typically 0.7–0.8)</li>
        <li>Add 20% buffer for future expansion</li>
        <li>Size to 110% of peak demand at design pressure</li>
      </ul>

      <h2>2. Oil-Free vs Oil-Lubricated</h2>
      <p>For pharma cGMP, oil-free screw compressors are the standard for product-contact applications. Oil-lubricated may be used for utility-grade air with appropriate filtration — but require careful risk assessment.</p>

      <h2>3. Air Quality per ISO 8573</h2>
      <ul>
        <li><strong>Solid particles:</strong> Class 1 or 2</li>
        <li><strong>Moisture:</strong> Class 2 (pressure dewpoint -40°C)</li>
        <li><strong>Oil:</strong> Class 1 or 2 (≤ 0.1 mg/m³)</li>
      </ul>

      <h2>4. Redundancy & Reliability</h2>
      <p>N+1 configuration. Auto-changeover. Buffer-tank sizing for 5–10 minutes of demand. Dual desiccant dryers with regeneration alternation.</p>

      <h2>5. Distribution Network</h2>
      <ul>
        <li>Loop topology, not branch — for even pressure distribution</li>
        <li>Stainless steel piping for product-contact applications</li>
        <li>Slope of 1–2% with condensate drains at low points</li>
        <li>Isolation valves at every user point</li>
      </ul>

      <h2>6. Monitoring & Validation</h2>
      <p>Continuous pressure, dewpoint, and oil-content monitoring. Periodic ISO 8573 sampling. Documented IQ/OQ/PQ with re-qualification on major changes.</p>

      <h2>Conclusion</h2>
      <p>A well-designed compressed air system runs invisibly for decades. A badly designed one becomes the chronic complaint of every plant manager.</p>

      <p>At H&G, we design, audit, and validate compressed air systems across pharma sectors. Contact us for capacity assessment or system redesign.</p>
    `
  },
  'pw-water-system-design': {
    title: 'PW Water System Design Basics for Pharma Engineers',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1582719471384-894fbb16e074.jpg',
    content: `
      <p class="lead">Purified Water (PW) is the most-used cGMP utility — and one of the most-cited in audit observations. A clear primer on loop design, sanitization strategy, distribution, and validation.</p>

      <h2>1. Source Water Treatment</h2>
      <p>Pre-treatment (multimedia filter, softener, carbon, ultrafiltration) → RO → EDI (or double-pass RO) → PW. Every step matters for microbial control and conductivity.</p>

      <h2>2. Storage & Distribution Loop</h2>
      <ul>
        <li>Stainless steel 316L electropolished</li>
        <li>Continuous re-circulation at &gt; 1 m/s velocity</li>
        <li>No dead legs &gt; 6 pipe diameters (USP) or 3D (stricter)</li>
        <li>Sloped piping with sanitary drains</li>
        <li>Orbital-welded joints (documented)</li>
      </ul>

      <h2>3. Sanitization Strategy</h2>
      <p>Three common methods:</p>
      <ul>
        <li><strong>Hot water sanitization</strong> at 80°C for 60 minutes — most common in India</li>
        <li><strong>Ozonization</strong> for ambient loops</li>
        <li><strong>Chemical sanitization</strong> as periodic backup</li>
      </ul>

      <h2>4. Monitoring Parameters</h2>
      <ul>
        <li>Conductivity: continuous, USP &lt;645&gt; compliant</li>
        <li>TOC: continuous, USP &lt;643&gt; compliant</li>
        <li>Microbial count: weekly sampling per defined plan</li>
        <li>Endotoxin: as required by product</li>
      </ul>

      <h2>5. Validation Strategy</h2>
      <p>3-phase validation:</p>
      <ol>
        <li>Phase 1 (14 days): Intensive sampling, daily testing</li>
        <li>Phase 2 (14 days): Demonstrate consistent control</li>
        <li>Phase 3 (1 year): Long-term performance with seasonal variations</li>
      </ol>

      <h2>Conclusion</h2>
      <p>PW system design rewards engineering discipline. Cut corners on velocity, dead legs, or sanitization and the loop will fight you for its entire operating life.</p>

      <p>At H&G, we design, qualify, and troubleshoot PW and WFI systems across pharma facilities in India and overseas. Contact us for system design or qualification support.</p>
    `
  },
  'select-right-pharma-consultant': {
    title: 'How to Select the Right Pharma Consultant or PMC',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '6 min read',
    image: '/images/external/photo-1554224155-8d04cb21cd6c.jpg',
    content: `
      <p class="lead">Selecting the right consultant or PMC partner is the single highest-leverage decision in your project. Brand names matter less than execution depth — here is the evaluation framework we recommend to every promoter and plant head.</p>

      <h2>1. Execution Track Record</h2>
      <p>Ask for projects of similar scope, similar geography, similar regulatory exposure. Visit at least two reference sites. Talk to plant heads — not just promoters.</p>

      <h2>2. Team Composition</h2>
      <ul>
        <li>Who will actually work on your project — and what is their pharma background?</li>
        <li>Is the senior engineer hands-on or only at kick-off?</li>
        <li>Is the team in-house or freelancer-stitched?</li>
      </ul>

      <h2>3. Technical Depth</h2>
      <p>Ask design-stage technical questions. The right consultant will discuss pressure cascades, dead legs, classification rationale, and validation strategy in your first meeting — not slides.</p>

      <h2>4. Audit Exposure</h2>
      <p>Has the team supported USFDA, EU cGMP, WHO, MHRA, or PMDA audits? Has any facility they designed received critical observations? How were they resolved?</p>

      <h2>5. Working Style Fit</h2>
      <ul>
        <li>Transparent decision documentation</li>
        <li>Willingness to escalate issues early</li>
        <li>Comfortable with promoter-driven environments</li>
        <li>Strong vendor pushback capability when needed</li>
      </ul>

      <h2>6. Commercial Clarity</h2>
      <p>Fixed-fee, time-and-material, or success-fee? Each model creates different incentives. Make sure the model aligns with your project complexity.</p>

      <h2>Red Flags</h2>
      <ul>
        <li>Generic proposals with no project-specific insight</li>
        <li>Heavy reliance on subcontractors for core work</li>
        <li>Reluctance to share past audit outcomes</li>
        <li>One-size-fits-all engagement models</li>
      </ul>

      <h2>Conclusion</h2>
      <p>The right consultant is not the cheapest, the largest, or the most marketed. It is the one whose senior team will stand next to you when the audit happens.</p>

      <p>At H&G, we welcome detailed evaluation. Contact us for a project-specific capability discussion.</p>
    `
  },
  'pmc-vs-epc-pharma': {
    title: 'PMC vs EPC — What Should Pharma Companies Choose?',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '7 min read',
    image: '/images/external/photo-1565793298595-6a879b1d9492.jpg',
    content: `
      <p class="lead">PMC vs EPC is not a one-size-fits-all choice. The right model depends on project complexity, internal team strength, risk appetite, and timeline. A side-by-side comparison for decision-makers.</p>

      <h2>What is PMC?</h2>
      <p>Project Management Consultancy. The PMC is the client's representative, managing multiple specialist vendors (HVAC, civil, electrical, equipment) on the client's behalf. Risk stays with the client.</p>

      <h2>What is EPC?</h2>
      <p>Engineering, Procurement, Construction. A single contractor takes turnkey responsibility for designing, procuring, and constructing the facility on a lump-sum or cost-plus basis. Risk transfers to the EPC.</p>

      <h2>When PMC Works Better</h2>
      <ul>
        <li>Project complexity is high — multiple technologies, sterile + non-sterile</li>
        <li>Client wants tight control over vendor selection</li>
        <li>Customisation is high — multiple product changeovers expected</li>
        <li>Client has decent in-house engineering team to support PMC</li>
      </ul>

      <h2>When EPC Works Better</h2>
      <ul>
        <li>Standardised facility, repeat design</li>
        <li>Client has limited in-house engineering bandwidth</li>
        <li>Timeline is aggressive, single-point accountability matters</li>
        <li>Risk transfer is more important than cost optimisation</li>
      </ul>

      <h2>Cost Comparison</h2>
      <p>PMC typically delivers 8–15% lower project cost than EPC for complex pharma projects — but requires more client management bandwidth. EPC offers cost certainty but at a risk premium.</p>

      <h2>Schedule Comparison</h2>
      <p>EPC delivers faster on standardised projects (single-point coordination). PMC delivers faster on complex projects (specialist vendors execute in parallel).</p>

      <h2>Quality Comparison</h2>
      <p>PMC generally produces higher-quality outcomes for pharma because the client selects best-in-class vendors per discipline. EPC quality depends entirely on the EPC's pharma capability.</p>

      <h2>The Hybrid Model</h2>
      <p>Many smart promoters use PMC for design + procurement, then EPC contracts for civil + utilities. Best of both worlds.</p>

      <h2>Conclusion</h2>
      <p>There is no universally right answer. The right model is the one that matches your project complexity, your team capability, and your risk philosophy.</p>

      <p>At H&G, we provide PMC services and advise on PMC vs EPC structuring for pharma projects. Contact us for a delivery-model assessment.</p>
    `
  },
  'lessons-25-years-pharma-engineering': {
    title: 'Lessons Learned from 25 Years in Pharma Engineering',
    category: 'Pharma Engineering',
    author: 'Raju Wagh',
    authorTitle: 'Pharma Engineering & PMC Lead',
    date: 'May 25, 2026',
    readTime: '8 min read',
    image: '/images/external/photo-1518770660439-4636190af475.jpg',
    content: `
      <p class="lead">After 25 years across HVAC, utilities, cleanrooms, and PMC, certain patterns repeat across every project. These are the lessons we wish every promoter, plant head, and project sponsor knew at the start.</p>

      <h2>1. Design Decisions Echo for 20 Years</h2>
      <p>The decisions taken in the first 90 days of a project — layout, classification, utility headers, automation strategy — define the next two decades of plant economics. Slow down at the start; speed up later.</p>

      <h2>2. The Cheapest Vendor is the Most Expensive</h2>
      <p>The cost difference at order placement is dwarfed by the cost of rework, delays, and audit observations. Buy capability, not price.</p>

      <h2>3. Audit-Readiness is a Daily Habit</h2>
      <p>Plants that are audit-ready every day pass audits effortlessly. Plants that prepare for audits scramble — and get observations. Build the systems for daily compliance, not periodic preparation.</p>

      <h2>4. People Matter More Than Systems</h2>
      <p>A great system run by a mediocre team underperforms a mediocre system run by a great team. Invest in operator training, supervisor capability, and engineering culture.</p>

      <h2>5. Documentation is the Engineering Deliverable</h2>
      <p>If it is not documented, it did not happen. The audit-defending hero of every plant is its documentation system — URS, FDS, DQ, IQ, OQ, PQ, change controls, deviations, CAPAs.</p>

      <h2>6. Maintenance is Not a Cost — It is Insurance</h2>
      <p>Every rupee saved on PM is paid back ten-fold in breakdown downtime. The best-run plants treat maintenance as a strategic function, not an overhead.</p>

      <h2>7. Energy Efficiency Pays Forever</h2>
      <p>Pharma plants are energy-intensive. Designs that prioritise energy efficiency (VFDs, heat recovery, smart controls) pay back in 18–36 months and keep paying for the next 25 years.</p>

      <h2>8. The Best Plants are Designed Around Operators</h2>
      <p>If the operator has to climb a ladder to change a HEPA filter, the filter will not be changed on time. Design for the operator, and the plant will run well.</p>

      <h2>9. Regulators Are Allies, Not Adversaries</h2>
      <p>Pharma regulation exists to protect patients. The best engineering teams treat regulators as partners — understanding their concerns, addressing them substantively, and building plants that deserve the licences they receive.</p>

      <h2>10. Build for the Plant You Will Have in 10 Years</h2>
      <p>Capacity expansion, product diversification, technology upgrades — these are not exceptions, they are the norm. Build modular, expandable, future-aware facilities.</p>

      <h2>Conclusion</h2>
      <p>25 years in pharma engineering teach you that the technical problems are usually solvable. The leadership, discipline, and culture problems are the ones that determine outcomes.</p>

      <p>At H&G, we bring this experience to every project — from concept to commissioning. Contact us to discuss your next facility, audit, or PMC engagement.</p>
    `
  }
};

const InsightArticlePage = () => {
  const { locale, slug } = useParams();
  const article = articles[slug];

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <SEO title="Article Not Found" description="The requested article could not be found." path={`/insights/${slug}`} />
        <div className="text-center">
          <h1 className="font-playfair text-3xl text-navy mb-4">Article Not Found</h1>
          <Link to={`/${locale}/insights`} className="text-teal hover:text-teal">
            ← Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = generateArticleSchema({
    title: article.title,
    excerpt: article.content.substring(0, 200),
    author: article.author,
    date: article.date,
    image: article.image
  });

  return (
    <div className="pt-20" data-testid="insight-article-page">
      <SEO 
        title={article.title}
        description={article.content.substring(0, 160).replace(/<[^>]*>/g, '')}
        path={`/insights/${slug}`}
        type="article"
        image={article.image}
        structuredData={articleSchema}
      />
      {/* Hero */}
      <section className="relative">
        <div className="h-[400px] relative">
          <img 
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-xl"
          >
            <Link 
              to={`/${locale}/insights`}
              className="inline-flex items-center text-teal hover:text-teal mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Insights
            </Link>
            
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal rounded-full text-sm font-medium mb-4">
              {article.category}
            </span>
            
            <h1 className="font-playfair text-3xl md:text-4xl text-navy mb-6">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-mid-grey">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {article.author}, {article.authorTitle}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {article.date}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {article.readTime}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_200px] gap-12">
            <motion.article
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-lg max-w-none
                prose-headings:font-playfair prose-headings:text-navy
                prose-p:text-mid-grey prose-p:leading-relaxed
                prose-a:text-teal prose-a:no-underline hover:prose-a:text-teal
                prose-strong:text-navy
                prose-ul:text-mid-grey prose-li:text-mid-grey
                [&_.lead]:text-xl [&_.lead]:text-navy [&_.lead]:font-medium [&_.lead]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            
            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <h4 className="font-semibold text-navy mb-4">Share</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-light-grey rounded-lg flex items-center justify-center text-navy hover:bg-teal hover:text-white transition-colors">
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a href="#" className="w-10 h-10 bg-light-grey rounded-lg flex items-center justify-center text-navy hover:bg-teal hover:text-white transition-colors">
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-3xl text-navy mb-4">
            Want to Learn More?
          </h2>
          <p className="text-mid-grey mb-6">
            Our advisors are ready to discuss how these insights apply to your organization.
          </p>
          <Link to={`/${locale}/contact`} className="btn-primary">
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InsightArticlePage;
